'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface CountryPin {
  id: string;
  name: string;
  x: number;
  y: number;
  students: number;
  universities: number;
  description: string;
}

interface InteractiveWorldMapProps {
  className?: string;
}

const InteractiveWorldMap = ({ className = '' }: InteractiveWorldMapProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [hoveredCountry, setHoveredCountry] = useState<string | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const countryPins: CountryPin[] = [
    {
      id: 'germany',
      name: 'Germany',
      x: 51,
      y: 32,
      students: 8500,
      universities: 45,
      description: 'Engineering & Research Excellence'
    },
    {
      id: 'singapore',
      name: 'Singapore',
      x: 77,
      y: 62,
      students: 3200,
      universities: 12,
      description: 'Business & Finance Hub'
    },
    {
      id: 'france',
      name: 'France',
      x: 48,
      y: 35,
      students: 6200,
      universities: 38,
      description: 'Arts & Cultural Studies'
    },
    {
      id: 'russia',
      name: 'Russia',
      x: 65,
      y: 28,
      students: 5800,
      universities: 28,
      description: 'Medicine & STEM Programs'
    },
    {
      id: 'china',
      name: 'China',
      x: 75,
      y: 40,
      students: 12000,
      universities: 52,
      description: 'Technology & Innovation'
    },
    {
      id: 'japan',
      name: 'Japan',
      x: 84,
      y: 42,
      students: 4500,
      universities: 35,
      description: 'Robotics & Engineering'
    },
    {
      id: 'vietnam',
      name: 'Vietnam',
      x: 76,
      y: 56,
      students: 2800,
      universities: 18,
      description: 'Emerging Education Market'
    },
    {
      id: 'philippines',
      name: 'Philippines',
      x: 80,
      y: 58,
      students: 3500,
      universities: 22,
      description: 'English-Medium Education'
    },
    {
      id: 'saudi-arabia',
      name: 'Saudi Arabia',
      x: 56,
      y: 46,
      students: 2100,
      universities: 15,
      description: 'Modern Scholarship Programs'
    },
    {
      id: 'dubai',
      name: 'Dubai',
      x: 60,
      y: 48,
      students: 4100,
      universities: 25,
      description: 'Luxury Business Education'
    },
    {
      id: 'south-korea',
      name: 'South Korea',
      x: 82,
      y: 40,
      students: 3800,
      universities: 30,
      description: 'K-Culture & Technology'
    }
  ];

  const handleCountryClick = (countryId: string) => {
    if (!isHydrated) return;
    setSelectedCountry(selectedCountry === countryId ? null : countryId);
  };

  const handleCountryHover = (countryId: string | null) => {
    if (!isHydrated) return;
    setHoveredCountry(countryId);
  };

  if (!isHydrated) {
    return (
      <section className={`py-20 bg-gradient-to-br from-primary/5 to-secondary/5 hidden ${className}`}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">Global Operations</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">Interactive world map loading...</p>
          </div>
          <div className="max-w-6xl mx-auto bg-muted rounded-2xl p-8 h-96 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/20 rounded-full mx-auto mb-4 animate-pulse"></div>
              <p className="text-muted-foreground">Loading interactive map...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const selectedPin = countryPins.find(pin => pin.id === selectedCountry);

  return (
    <section className={`py-20 bg-gradient-to-br from-primary/5 to-secondary/5 hidden ${className}`}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
            Global Operations
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Discover our worldwide presence. Click on any destination to explore education opportunities and partnerships.
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="relative bg-white rounded-2xl p-8 shadow-elevated overflow-hidden">
            <div className="relative w-full h-96 md:h-[500px]">
              <svg
                viewBox="0 0 100 70"
                className="w-full h-full"
                style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)' }}
              >
                <path
                  d="M10 15 Q15 12 20 15 L25 13 L30 16 L35 14 L40 17 L45 15 L50 18 L55 16 L60 19 L65 17 L70 20 L75 18 L80 21 L85 19 L90 22 L90 55 Q85 58 80 55 L75 57 L70 54 L65 56 L60 53 L55 55 L50 52 L45 54 L40 51 L35 53 L30 50 L25 52 L20 49 L15 51 Q10 48 10 45 Z"
                  fill="#e2e8f0"
                  stroke="#cbd5e1"
                  strokeWidth="0.3"
                  opacity="0.6"
                />
                <path
                  d="M15 20 L20 18 L25 21 L30 19 L35 22 L40 20 L45 23 L50 21 L55 24 L60 22 L65 25 L70 23 L75 26 L80 24 L85 27 L85 50 L80 52 L75 49 L70 51 L65 48 L60 50 L55 47 L50 49 L45 46 L40 48 L35 45 L30 47 L25 44 L20 46 L15 43 Z"
                  fill="#f1f5f9"
                  stroke="#cbd5e1"
                  strokeWidth="0.3"
                  opacity="0.4"
                />
                <ellipse cx="30" cy="30" rx="8" ry="6" fill="#e2e8f0" opacity="0.5" />
                <ellipse cx="70" cy="35" rx="12" ry="9" fill="#e2e8f0" opacity="0.5" />
                <ellipse cx="78" cy="55" rx="6" ry="5" fill="#e2e8f0" opacity="0.5" />
                <ellipse cx="50" cy="60" rx="10" ry="7" fill="#e2e8f0" opacity="0.5" />
                <ellipse cx="20" cy="50" rx="7" ry="5" fill="#e2e8f0" opacity="0.5" />
              </svg>

              {countryPins.map((pin) => (
                <button
                  key={pin.id}
                  onClick={() => handleCountryClick(pin.id)}
                  onMouseEnter={() => handleCountryHover(pin.id)}
                  onMouseLeave={() => handleCountryHover(null)}
                  className="absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-full"
                  style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
                  aria-label={`View ${pin.name} details`}
                >
                  <div className={`relative ${selectedCountry === pin.id ? 'scale-125' : ''}`}>
                    <div className={`w-5 h-5 rounded-full border-3 border-white shadow-lg transition-all duration-300 ${selectedCountry === pin.id ? 'bg-accent scale-125' : hoveredCountry === pin.id ? 'bg-primary scale-110' : 'bg-secondary'}`}>
                      <div className="absolute inset-0 rounded-full animate-ping bg-current opacity-30"></div>
                    </div>
                    
                    {(hoveredCountry === pin.id || selectedCountry === pin.id) && (
                      <div className="absolute top-7 left-1/2 transform -translate-x-1/2 whitespace-nowrap z-10">
                        <div className="bg-card px-3 py-2 rounded-lg shadow-elevated border border-border">
                          <p className="text-sm font-semibold text-foreground">{pin.name}</p>
                          <p className="text-xs text-muted-foreground">{pin.students} students</p>
                        </div>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>

            {selectedPin && (
              <div className="mt-8 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-xl p-6 border border-border shadow-sm">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-foreground mb-2">{selectedPin.name}</h3>
                    <p className="text-muted-foreground">{selectedPin.description}</p>
                  </div>
                  <button
                    onClick={() => setSelectedCountry(null)}
                    className="p-2 hover:bg-muted rounded-lg transition-colors duration-300"
                    aria-label="Close details"
                  >
                    <Icon name="XMarkIcon" size={20} className="text-muted-foreground" />
                  </button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="text-center p-4 bg-white rounded-lg">
                    <div className="text-2xl font-bold text-primary">{selectedPin.students}</div>
                    <div className="text-sm text-muted-foreground">Students Placed</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg">
                    <div className="text-2xl font-bold text-secondary">{selectedPin.universities}</div>
                    <div className="text-sm text-muted-foreground">Partner Universities</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg">
                    <div className="text-2xl font-bold text-conversion">98%</div>
                    <div className="text-sm text-muted-foreground">Success Rate</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg">
                    <div className="text-2xl font-bold text-accent">95%</div>
                    <div className="text-sm text-muted-foreground">Visa Approval</div>
                  </div>
                </div>

                <div className="flex gap-4">
                  <Link
                    href="/destinations"
                    className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-3 rounded-lg font-semibold text-center transition-colors duration-300"
                  >
                    Explore Programs
                  </Link>
                  <Link
                    href="/consultation-booking"
                    className="flex-1 bg-secondary hover:bg-secondary/90 text-secondary-foreground px-6 py-3 rounded-lg font-semibold text-center transition-colors duration-300"
                  >
                    Book Consultation
                  </Link>
                </div>
              </div>
            )}
          </div>

          <div className="mt-8 text-center">
            <p className="text-muted-foreground mb-4">
              We provide comprehensive education services across 9 countries worldwide
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {countryPins.map((pin) => (
                <button
                  key={pin.id}
                  onClick={() => handleCountryClick(pin.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    selectedCountry === pin.id
                      ? 'bg-accent text-accent-foreground shadow-lg scale-105'
                      : 'bg-white text-foreground hover:bg-primary hover:text-primary-foreground border border-border'
                  }`}
                >
                  {pin.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InteractiveWorldMap;